import { solid, regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FileSaver from 'file-saver';
import React, { useEffect, useState } from 'react'
import { memo } from 'react';
import { Alert, Container, Modal, Button, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router';
import { Models } from '../../api/Models';
import AppLoader from '../../components/common/AppLoader';
import AppSearch from '../../components/common/AppSearch';
import AssetItem from '../../components/list-items/AssetItem';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { accessLevelOptions } from '../../utils';
import { Checkbox, Pagination, Select } from "antd";
import { debounce } from "lodash";
import { useMemo } from "react";
import { useAuth } from "../../contexts/auth";
import { BdaModel } from '../../models/BdaModel';
import { PagedResults } from '../../models/generics';
import { CheckboxChangeEvent } from 'antd/es/checkbox';

const statusOptions = [
  { label: "Virtual", value: "false", icon: solid("lock") },
  { label: "Created", value: "true", icon: regular("check-circle") },
];


function ModelsList() {
  const [models, setModels] = useState<BdaModel[]>();
  const [totalModels, setTotalModels] = useState<number>();
  const [error, setError] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>();
  const [accessLevelFilter, setAccessLevelFilter] = useState<string>();
  const [myModels, setMyModels] = useState(false);

  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const [sortDesc, setSortDesc] = useState(true);

  const [modelToDelete, setModelToDelete] = useState<BdaModel>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { loggedUser } = useAuth();
  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const navigate = useNavigate();

  useEffect(() => {
    reset();
    // eslint-disable-next-line
  }, [currentPage]);

  // handling current page higher than actual available pages edge case
  useEffect(() => {
    if (currentPage > 1) setCurrentPage(1);
    else reset();
    // eslint-disable-next-line
  }, [itemsPerPage, sortDesc, searchText, statusFilter, accessLevelFilter, myModels]);

  const getModels = () => {
    const params = new URLSearchParams();
    params.set("page", `${currentPage}`);
    params.set("size", `${itemsPerPage}`);
    params.set("sort", "id");
    params.set("desc", `${sortDesc}`);
    if (myModels) params.set("createdBy", loggedUser!.userId!);
    if (statusFilter) params.set("created", statusFilter);
    if (accessLevelFilter) params.set("accessLevel", accessLevelFilter);
    if (searchText.length) params.set("freeText", searchText);

    Models.getModels(params)
      .then(
        ({ data: modelsPages }) => {
          setModels((modelsPages as PagedResults<BdaModel>).content);
          setTotalModels((modelsPages as PagedResults<BdaModel>).pageMetadata.totalElements);
        },
        (err) => {
          showToast('models-error', 'Could not get models', 'Models Error', 'danger');
          setModels([]);
          setError(true);
        }
      );
  }

  const navigateToCreation = () => navigate('/models/create');
  // const navigateToDetail = (dataset) => () => navigate(`/models/${dataset.id}`);
  const handleMyModelsChange = (evt: CheckboxChangeEvent) => setMyModels(evt.target.checked);
  const changeSortMode = () => setSortDesc(!sortDesc);
  const handleClose = () => { setShowDeleteModal(false); setModelToDelete(undefined) };

  // const navigateToEdit = (app) => (evt) => {
  //   evt.stopPropagation();
  //   navigate(`/models/${app.id}/edit`);
  // };

  const reset = () => {
    setError(false);
    setModels(undefined);
    getModels();
  }

  const openDeleteModal = (model: BdaModel) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    setShowDeleteModal(true);
    setModelToDelete(model);
  }

  const deleteModel = () => {
    showLoader();

    Models.deleteModel(modelToDelete!.id)
      .then(
        () => {
          hideLoader();
          reset();
        },
        () => {
          hideLoader();
          showToast('models-error', 'Could not delete model', 'Model Error', 'danger');
        }
      )
  }

  const handleFilterChange = (filter: 'search' | 'accessLevel' | 'status') => (evt: any) => {
    const value = filter === "search" ? evt.target.value : evt;

    switch (filter) {
      case 'search':
      default:
        setSearchText(value);
        break;

      case 'accessLevel':
        setAccessLevelFilter(value);
        break;

      case 'status':
        setStatusFilter(value);
        break;
    }
  }

  const debounceSearchChange = useMemo(() => debounce(handleFilterChange("search"), 500), []);

  const handleItemsPerPageChange = (page: number, size: number) => {
    changePage(page);
    setItemsPerPage(size);
  }
  const changePage = (page: number) => setCurrentPage(page);
  const modelDetail = (model: BdaModel) => () => navigate(`/models/${model.id}`);

  const downloadModel = (model: BdaModel) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    showLoader();

    Models.downloadModel(model.id)
      .then(
        ({ data: modelToDownload }) => {
          hideLoader();
          const blob = new Blob([modelToDownload], { type: 'application/zip' });
          FileSaver.saveAs(blob, `${model.name}.zip`);
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('models-error', message, `Model Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  // cleanup debounced handler
  useEffect(() => {
    return () => {
      debounceSearchChange.cancel();
    }
  }, [debounceSearchChange]);

  const renderModels = () => {
    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occured, please try again later.</span>
      </Alert>
    );

    return models!.length === 0
      ? (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No models found.</span>
        </Alert>
      )
      : (
        <Row>
          {
            models!.map(model => (
              <Col xs={12} sm={6} md={4} lg={3} className="p-2" key={model.id}>
                <AssetItem
                  type="model"
                  asset={model}
                  onDownload={downloadModel(model)}
                  onClick={modelDetail(model)}
                  onDelete={openDeleteModal(model)}
                />
              </Col>
            ))
          }
        </Row>
      )
  }

  const renderAsyncContent = () => {
    return models
      ? renderModels()
      : (
        <div className="d-flex justify-content-center py-5">
          <AppLoader size={300} />
        </div>
      );
  }

  const renderToolbar = () => {
    return (
      <>
        <Row className="mb-2 align-items-center">
          <Col>
            <h4 className="mb-0">Models List</h4>
          </Col>

          <Col xs={6} md={4} className="text-end">
            <Button size="sm" variant="success" onClick={navigateToCreation} disabled>
              <FontAwesomeIcon icon={solid('plus')} />
              <span className="d-none d-sm-inline ms-2">Create Model</span>
            </Button>
            {/* <Button size="sm" variant="primary" className="ms-2">
              <FontAwesomeIcon icon={solid('cloud-upload-alt')} />
            </Button> */}
          </Col>
        </Row>
        <Row>
          <Col className="mb-2" md={3}>
            <AppSearch placeholder="Search model..." onChange={debounceSearchChange} />
          </Col>
          <Col md={2} className="mb-2">
            <Select
              placeholder="Select status"
              value={statusFilter}
              allowClear
              onChange={handleFilterChange('status')}
              className="w-100"
            >
              {statusOptions.map((opt, index) => (
                <Select.Option key={index} value={opt.value} label={opt.label}>
                  <div>
                    <FontAwesomeIcon icon={opt.icon} fixedWidth />
                    <span className="ms-2">{opt.label}</span>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col md={2} className="mb-2">
            <Select
              placeholder="Select privacy level"
              value={accessLevelFilter}
              allowClear
              onChange={handleFilterChange('accessLevel')}
              className="w-100"
            >
              {accessLevelOptions
                .filter(option => window.__env.ALLOWED_ACCESS_LEVELS ? window.__env.ALLOWED_ACCESS_LEVELS.includes(option.value) : true)
                .map((option, index) => (
                  <Select.Option key={index} value={option.value} label={option.label}>
                    <div>
                      <FontAwesomeIcon icon={option.icon} fixedWidth />
                      <span className="ms-2">{option.label}</span>
                    </div>
                  </Select.Option>
                ))}
            </Select>
          </Col>
        </Row>
        <Row>
          <Col md={2}>
            <Checkbox checked={myModels} onChange={handleMyModelsChange}>My Models</Checkbox>
            {/* <Checkbox className="ms-3" checked={defaultOnly} onChange={handleDefaultOnlyChange}>Default Only</Checkbox>
            <Checkbox className="ms-3" checked={activeSchedules} onChange={handleSchedulesChange}>Active Schedules</Checkbox> */}
          </Col>
          <Col className="text-center">
            <Pagination
              disabled={!models}
              total={totalModels || 0}
              showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
              pageSize={itemsPerPage}
              current={currentPage}
              onChange={changePage}
              onShowSizeChange={handleItemsPerPageChange}
            />
          </Col>
          <Col md={2}>
            <Button size="sm" variant="light" className="w-100 border" onClick={changeSortMode}>
              <FontAwesomeIcon icon={sortDesc ? solid('sort-numeric-down-alt') : solid('sort-numeric-down')} />
              <span className="ms-2">Sort: <b>{sortDesc ? "descending" : "ascending"}</b></span>
            </Button>
          </Col>
        </Row>
      </>
    )
  }

  return (
    <Container fluid="md" className="mt-3">
      {renderToolbar()}

      <hr />

      {renderAsyncContent()}

      <Modal show={showDeleteModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Model</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete model <b>{modelToDelete && modelToDelete.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleClose}>
            Close
          </Button>
          <Button variant="danger" size="sm" onClick={deleteModel}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default memo(ModelsList);
